const extend = require('xtend/mutable');
const q = require('queried');
const ajax = require('component-ajax');
const on = require('emmy/on');
const emit = require('emmy/emit');
const serialize = require('form-serialize');
const domify = require('domify');
const cookie = require('cookie-cutter');
const forEach = require('object-foreach');
const events = require('events-mixin');
const setCaretPosition = require('caret-position2/set');
const phrases = require('./phrases');
const _ = require('./i18n');
const { showLoginPopup } = require('./utils');

const csrfToken = cookie.get('csrftoken');

/**
 * Comment instance constructor
 * @param {object} options
 * @constructor
 */
function Comment(options) {
  extend(this, options);
  this.init();
}

/**
 * Set replies form, default for all comments
 * @param {CommentsForm} form
 */
Comment.setRepliesForm = function (form) {
  Comment.prototype.repliesForm = form;
};

extend(Comment.prototype, {
  /**
   * last loaded item id (for ajax request)
   * @type {String}
   */
  removeUrl: '/comments/{id}/',

  /**
   * url of comment restore api
   * @type {String}
   */
  restoreUrl: '/comments/{id}/',

  /**
   * ajax method (for testing)
   * @type {function}
   */
  ajax,

  /**
   * replies list element selector
   * @type {String}
   */
  repliesSelector: '.comment-responses',

  /**
   * Initialize the instance
   */
  init() {
    this.actualView = q('.comment-actual-view', this.el);
    this.removedView = q('.comment-restore-view', this.el);
    this.content = q('.comment-content', this.el);

    const authorEl = q('.comment-author-name', this.el);
    if (authorEl) this.author = authorEl.textContent.trim();

    if (this.el) {
      this.id = +this.el.getAttribute('data-id');

      if (q('.comment-deleted-phrase', this.el)) {
        this.initDeleted();
      }
    }

    this.repliesEl = q(this.repliesSelector, this.el);
    if (this.repliesEl) {
      this.repliesList = new CommentsList({
        el: this.repliesEl,
        timeDirection: 'after',
        // repliesForm prop should be set on prototype
        form: this.repliesForm,
        loadAll: true,
      });
    }

    if (this.el) {
      this.bindEvents();
    }
  },

  /**
   * Bind comment's DOM events
   */
  bindEvents() {
    this.events = events(this.el, this);

    // Do not remake it to delegation as child comments will affect parents
    on(q('.comment-delete-link', this.el), 'click', (e) => this.remove(e));
    on(q('.comment-restore-link', this.el), 'click', (e) => this.restore(e));
    on(q('.comment-reply-link', this.el), 'click', (e) => this.reply(e));
  },

  /**
   * Remove comment action
   */
  remove() {
    const self = this;
    self.ajax({
      url: self.removeUrl.replace('{id}', self.id),
      data: JSON.stringify({
        is_deleted: true,
      }),
      contentType: 'application/json',
      headers: {
        'X-CSRFToken': csrfToken,
      },
      type: 'PATCH',
      success: () => {
        self.removedView.removeAttribute('hidden');
        self.actualView.setAttribute('hidden', 'hidden');
      },
    });
  },

  /**
   * Restore comment right after deletion
   */
  restore() {
    this.ajax({
      url: this.restoreUrl.replace('{id}', this.id),
      data: JSON.stringify({
        is_deleted: false,
      }),
      contentType: 'application/json',
      headers: {
        'X-CSRFToken': csrfToken,
      },
      type: 'PATCH',
      success: () => {
        this.removedView.setAttribute('hidden', 'hidden');
        this.actualView.removeAttribute('hidden');
      },
    });
  },

  /**
   * 'Reply' button click handler
   */
  reply() {
    this.el.insertBefore(this.repliesForm.el, this.content.nextSibling);
    this.repliesForm.commentsList = this.repliesList;
    this.repliesForm.el.removeAttribute('hidden');

    q('[name="reply_to"]', this.repliesForm.el).value = this.id;
    this.repliesForm.setReplyPrefix(`${this.author}, `);
  },

  /**
   * If comment is a deleted stub, make it look good
   */
  initDeleted() {
    const textEl = q('.comment-delete-stub-text', this.el);
    const authorEl = q('.comment-delete-stub-author', this.el);
    textEl.textContent = phrases.getRandomPhrase();
    authorEl.textContent = `– ${phrases.getRandomAuthor()}`;
  },
});

/**
 * CommentsList class
 * @constructor
 * @param {object} options
 */
function CommentsList(options) {
  extend(this, options);
  this.init();
}

extend(CommentsList.prototype, {

  /**
   * Last loaded item id (for ajax request)
   * @type {number|null}
   */
  lastId: null,

  /**
   * Ajax method (for testing)
   * @type {function}
   */
  ajax,

  /**
   * Url for loading more comments
   * @type {String}
   */
  url: '/widgets/comments/',

  /**
   * Does list's ribbon goes forward or backward?
   * So we can set what we request - items 'before' some id
   * or items 'after' some id
   * @type {String}
   */
  timeDirection: 'before',

  /**
   * How much items we've loaded by the moment
   * @type {Number}
   */
  loadedAmount: 0,

  /**
   * Initialize comments list
   */
  init() {
    this.type_id = +this.el.getAttribute('data-type-id');
    this.object_id = +this.el.getAttribute('data-object-id');
    this.thread_id = +this.el.getAttribute('data-thread');
    this.amount = +this.el.getAttribute('data-amount');

    this.loadMoreButton = q('> .comments-load-more', this.el);
    if (this.initialLoad) {
      this.loadInitialComments();
    }
    this.initChildren();
    this.bindEvents();
  },

  /**
   * Bind list's DOM events
   */
  bindEvents() {
    this.events = events(this.el, this);
    this.events.bind('click .comments-load-more', 'loadMore');
  },

  /**
   * Load more comments via ajax
   */
  loadMore(event) {
    // prevent parent view from loading
    if (event) {
      event.stopPropagation();
    }

    if (this.loadMoreButton) {
      this.loadMoreButton.classList.add('preloading');
    }

    const data = {
      object_id: this.object_id,
      type_id: this.type_id,
    };
    if (this.thread_id) data.thread_id = this.thread_id;
    if (this.loadAll) data.limit = this.amount - this.loadedAmount;
    data[this.timeDirection] = this.lastId;

    this.ajax({
      url: this.url,
      type: 'GET',
      data,
      contentType: 'application/json',
      success: (response) => {
        // stop preloader
        if (this.loadMoreButton) {
          this.loadMoreButton.classList.remove('preloading');
        }

        // remove temporary comments
        q.all('.comment-temporary', this.el).forEach((item) => {
          item.parentNode.removeChild(item);
        });

        // render items
        const renderedNode = domify(`<div>${response}</div>`);
        const children = [].slice.call(renderedNode.children);
        children.forEach((item) => {
          const comment = new Comment({
            el: item,
          });
          this.add(comment);
        });
      },
    });
  },

  /**
   * Add a comment to the list
   * @param {Comment} comment
   * @param {object} options - settings of the comment addition
   * @param {boolean} options.prepend   - push the comment to the beginning
   *                                      of the list
   * @param {boolean} options.temporary - add comment, but remove it
   *                                      on next load
   */
  add(comment, options) {
    options = options || {};

    if (options.prepend) {
      this.el.insertBefore(comment.el, this.el.firstChild);
    } else {
      this.el.appendChild(comment.el);
      if (this.loadMoreButton) {
        this.el.appendChild(this.loadMoreButton);
      }
    }

    if (!options.temporary) {
      this.applyCommentId(comment);
      this.increaseLoadedAmount();
    } else {
      comment.el.classList.add('comment-temporary');
    }
  },

  /**
   * Check the comment's id and set last id according to it
   * if it's more actual than current last id
   * @param  {Comment} comment
   */
  applyCommentId(comment) {
    if (
      this.lastId === null
      || (this.timeDirection == 'before' && +comment.id < this.lastId)
      || (this.timeDirection == 'after' && +comment.id > this.lastId)
    ) {
      this.lastId = +comment.id;
    }
  },

  /**
   * Update loaded comment state after new comment addition
   */
  increaseLoadedAmount() {
    this.loadedAmount++;

    if (this.loadedAmount >= this.amount && this.loadMoreButton) {
      this.loadMoreButton.setAttribute('hidden', 'hidden');
    }
  },

  /**
   * Run through markup and initialize the comments in it
   */
  initChildren() {
    [].forEach.call(this.el.children, (item) => {
      if (!item.classList.contains('comment')) return;

      const comment = new Comment({
        el: item,
        repliesList: this,
      });

      this.applyCommentId(comment);
      this.increaseLoadedAmount();
    });
  },

  loadInitialComments() {
    if (!this.el.children.length) {
      const data = {
        object_id: this.el.getAttribute('data-object-id'),
        type_id: this.el.getAttribute('data-type-id'),
      };

      ajax({
        url: this.url,
        type: 'GET',
        data,
        success: (response) => {
          this.el.innerHTML = response;
          if (!this.loadMoreButton) {
            this.loadMoreButton = q('> .comments-load-more', this.el);
          }
          if (!this.amount) {
            this.amount = +q('.commentsCount', this.el).getAttribute('data-amount');
          }
          this.initChildren();
        },
      });
    }
  },
});

/**
 * Comments Form class
 * @param {object} options
 * @constructor
 */
function CommentsForm(options) {
  extend(this, options);
  this.init();
}

extend(CommentsForm.prototype, {

  /**
   * How to alert validation errors
   * useful for testing
   * @type {null|function}
   */
  alert: window.alert.bind(window),

  /**
   * Ajax method (for testing)
   * @type {function}
   */
  ajax,

  /**
   * translate method (for testing)
   * @type {function}
   */
  _,

  /**
   * Storage of form's state
   * Should implement Storage interface
   * @see https://developer.mozilla.org/ru/docs/Web/API/Storage
   * @type {Storage}
   */
  storage: sessionStorage,

  /**
   * Key where we store state
   * @type {String}
   */
  storageKey: 'savedComment',

  /**
   * Events on which we update form's state
   * @type {String}
   */
  updateEvents: 'input keypress keyup paste change',

  /**
   * Maximum symbols user can type in the form's textarea
   * @type {Number}
   */
  maxSymbols: 1000,

  /**
   * Prefix to a reply revealing its addressee
   * @type {String}
   */
  replyPrefix: '',

  /**
   * Initialize form instance
   */
  init() {
    this.textarea = q('textarea', this.el);
    this.rating = q.all('[name="rating"]', this.el);
    this.replyTo = q('[name="reply_to"]', this.el);
    this.symbolsLeft = q('.comments-reply-symbols-left', this.el);
    this.title = q('.comments-title');
    this.submitButton = q('[type="submit"]', this.el);
    this.bindEvents();
    this.countSymbols();
  },

  /**
   * Bind item's DOM events
   */
  bindEvents() {
    this.events = events(this.el, this);
    this.updateEvents.split(' ').forEach((event) => {
      this.events.bind(event, 'save');
      this.events.bind(event, 'countSymbols');
    });
    this.events.bind('submit', 'submit');
  },

  /**
   * Save form's state to storage
   */
  save() {
    const data = {
      path: document.location.pathname,
      text: this.textarea.value,
      replyTo: this.replyTo.value,
      formId: this.el.id,
    };

    const chosenRatingEl = q('[name="rating"]:checked', this.el);
    const rating = chosenRatingEl && chosenRatingEl.value;
    if (rating) {
      data.rating = rating;
    }

    if (this.storage) {
      this.storage.setItem(this.storageKey, JSON.stringify(data));
    }
  },

  /**
   * Restore form's state from storage
   */
  restore() {
    let restored = null;
    if (this.storage) {
      restored = this.storage.getItem(this.storageKey);
    }
    if (restored) {
      const restoredObj = JSON.parse(restored);
      if (
        restoredObj.path == document.location.pathname
        && restoredObj.formId == this.el.id
      ) {
        if (restoredObj.replyTo) {
          const commentEl = q(`.comment[data-id="${restoredObj.replyTo}"]`);
          const replyLink = q('.comment-reply-link', commentEl);
          emit(replyLink, 'click', null, true);
        }

        const { rating } = restoredObj;
        if (rating) {
          const starElement = q(`[name="rating"][value="${rating}"]`, this.el);
          if (starElement) {
            starElement.setAttribute('checked', 'checked');
          }
        }

        this.el.removeAttribute('hidden');
        this.textarea.value = restoredObj.text;
        this.countSymbols();

        window.location.hash = 'comments';
      }
    }
  },

  /**
   * [countSymbols description]
   */
  countSymbols() {
    if (!this.symbolsLeft) return;

    const symbolsLeftNumber = this.maxSymbols - this.textarea.value.length;

    this.symbolsLeft.textContent = symbolsLeftNumber;

    if (symbolsLeftNumber >= 0) {
      this.symbolsLeft.classList.remove('exceeded');
    } else {
      this.symbolsLeft.classList.add('exceeded');
    }
  },

  loginHandler() {
    showLoginPopup({
      reason: 'comment',
    });
  },

  /**
   * Send form's content to server via ajax
   */
  submit(event) {
    if (event) {
      event.preventDefault();
    }

    if (this.submitButton.classList.contains('comments-reply-submit-inactive')) {
      this.loginHandler();
      return;
    }

    q.all('.comment-error', this.el).forEach((el) => {
      el.parentNode.removeChild(el);
    });

    if (this.validate) {
      const isValid = this.validate.call(this);
      if (!isValid) return;
    }

    if (this.submitButton) {
      this.submitButton.setAttribute('disabled', 'disabled');
    }

    this.ajax({
      type: 'POST',
      data: serialize(this.el, { hash: true }),
      url: this.el.action,
      success: (html) => {
        // create and add new comment
        const el = domify(html);
        const comment = new Comment({
          el,
          repliesList: this.commentsList,
        });
        this.commentsList.add(comment, {
          prepend: this.prepend,
          temporary: !this.prepend,
        });

        // reset
        this.textarea.value = '';
        if (this.storage) {
          this.storage.removeItem(this.storageKey);
        }

        q.all('[name="rating"]', this.el).forEach((item) => {
          item.checked = false;
        });

        if (this.hideOnSubmit) this.el.setAttribute('hidden', 'hidden');

        this.countSymbols();

        if (this.submitButton) {
          this.submitButton.removeAttribute('disabled');
        }
      },
      error: (data) => {
        try {
          const response = JSON.parse(data.response);

          forEach(response, (fieldValue, fieldKey) => {
            const element = q(`[name="${fieldKey}"]`, this.el);
            this.showError(element || this.textarea, fieldValue[0]);
          });

          if (this.submitButton) {
            this.submitButton.removeAttribute('disabled');
          }
        } catch (e) {
          // pass
        }
      },
    });
  },

  /**
   * Validate the form
   * Can be assigned to null to disable validation
   * @return {boolean} is form valid
   */
  validate() {
    const errors = [];
    const text = this.textarea.value;

    // check is the comment empty
    if (text.trim() === this.replyPrefix.trim()) {
      errors.push(this._('Text field is required'));
    }

    if (this.rating && this.rating.length) {
      let checked = false;

      this.rating.forEach((item) => {
        if (item.checked) checked = true;
      });

      if (!checked) {
        errors.push(this._('Rating is required'));
      }
    }

    if (errors.length && this.alert) {
      this.alert(errors.join('\n'));
    }

    return !errors.length;
  },

  setReplyPrefix(prefix) {
    this.replyPrefix = prefix;
    this.textarea.value = prefix;
    setCaretPosition(this.textarea, prefix.length);
  },

  showError(element, text) {
    if (element) {
      const errorEl = document.createElement('div');
      errorEl.className = 'comment-error';
      errorEl.textContent = text;
      element.parentNode.insertBefore(errorEl, element.nextSibling);
    }
  },
});

module.exports = {
  Comment,
  CommentsList,
  CommentsForm,
};
